// src/components/common/SimpleRichTextEditor.jsx
import React, { useRef, useEffect, useState } from 'react';
import { Bold, Italic, Underline, List, ListOrdered } from 'lucide-react';

const SimpleRichTextEditor = ({
  value = '',
  onChange,
  placeholder = 'Enter your text here...',
  disabled = false,
  className = '',
  minHeight = '200px'
}) => {
  const editorRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);

  // Initialize editor content when value changes from outside
  useEffect(() => {
    if (editorRef.current && !isFocused) {
      // Only update if not currently editing to prevent cursor jumping
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value || '';
      }
    }
  }, [value, isFocused]);

  const handleInput = () => {
    if (editorRef.current && onChange) {
      const content = editorRef.current.innerHTML;
      onChange(content);
    }
  };

  const executeCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput(); // Trigger onChange after command
  };

  const ToolbarButton = ({ onClick, children, title, isActive = false }) => (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        onClick();
      }}
      onMouseDown={(e) => e.preventDefault()} // Prevent losing focus
      title={title}
      disabled={disabled}
      className={`p-2 rounded hover:bg-blue-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
        isActive ? 'bg-blue-100 text-blue-600' : 'text-gray-700'
      }`}
    >
      {children}
    </button>
  );

  return (
    <div className={`simple-rich-text-editor ${className}`}>
      {/* Toolbar */}
      <div className="flex items-center gap-1 p-2 border border-gray-300 border-b-0 bg-gray-50 rounded-t-lg">
        <ToolbarButton
          onClick={() => executeCommand('bold')}
          title="Bold (Ctrl+B)"
        >
          <Bold size={18} />
        </ToolbarButton>
        
        <ToolbarButton
          onClick={() => executeCommand('italic')}
          title="Italic (Ctrl+I)"
        >
          <Italic size={18} />
        </ToolbarButton>
        
        <ToolbarButton
          onClick={() => executeCommand('underline')}
          title="Underline (Ctrl+U)"
        >
          <Underline size={18} />
        </ToolbarButton>

        <div className="w-px h-6 bg-gray-300 mx-1"></div>

        <ToolbarButton
          onClick={() => executeCommand('insertUnorderedList')}
          title="Bullet List"
        >
          <List size={18} />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => executeCommand('insertOrderedList')}
          title="Numbered List"
        >
          <ListOrdered size={18} />
        </ToolbarButton>
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable={!disabled}
        onInput={handleInput}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          setIsFocused(false);
          handleInput(); // Ensure final content is saved on blur
        }}
        className={`border border-gray-300 rounded-b-lg p-3 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100 overflow-auto ${
          disabled ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'
        }`}
        style={{
          minHeight: minHeight,
          fontSize: '14px',
          lineHeight: '1.6'
        }}
        data-placeholder={placeholder}
        suppressContentEditableWarning={true}
      />

      <style>
        {`
          .simple-rich-text-editor [contenteditable]:empty:before {
            content: attr(data-placeholder);
            color: #9ca3af;
            pointer-events: none;
            position: absolute;
          }

          .simple-rich-text-editor [contenteditable] {
            position: relative;
          }

          .simple-rich-text-editor [contenteditable]:focus {
            outline: none;
          }

          /* Style lists properly */
          .simple-rich-text-editor ul,
          .simple-rich-text-editor ol {
            margin: 0.5em 0;
            padding-left: 2em;
          }

          .simple-rich-text-editor li {
            margin: 0.25em 0;
          }

          /* Style formatted text */
          .simple-rich-text-editor strong,
          .simple-rich-text-editor b {
            font-weight: bold;
          }

          .simple-rich-text-editor em,
          .simple-rich-text-editor i {
            font-style: italic;
          }

          .simple-rich-text-editor u {
            text-decoration: underline;
          }
        `}
      </style>
    </div>
  );
};

export default SimpleRichTextEditor;
